/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Dashboard, { DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { TimeRangeSelector } from '@g17eco/molecules/time-range-selector';
import { useGetDataTransferComparisonQuery, useGetInitiativeAppIntegrationQuery, useListSubConnectionsQuery, useLazyGetDataTransferExportQuery } from '@api/initiative-app-integrations';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { CTAdminBreadcrumbs } from '../../../components/breadcrumbs/CTAdminBreadcrumbs';
import { ROUTES } from '@constants/routes';
import { generateUrl } from '@routes/util';
import { DataTransferTable } from './DataTransferTable';
import { DataTransferQuestionView } from './DataTransferQuestionView';
import { downloadBlob } from '@utils/downloadBlob';
import { DataTransferCsvExport } from './DataTransferCsvExport';
import { useDataTransferParams } from './useDataTransferParams';
import { applyFilters, renderNoticeAlerts } from './data-transfer-utils';
import { DataTransferFilterBar } from './DataTransferFilterBar';
import { DataTransferSummaryCards } from './DataTransferSummaryCards';

export const DataTransferPage = () => {
  const { code, initiativeId } = useParams<{ code: string; initiativeId: string }>();
  const isUserStaff = useAppSelector(isStaff);
  const { childInitiativeId, startDate, endDate, dateRange, timeRange, groupMode, search, filters, setChildInitiativeId, setDateRange, setGroupMode, setSearch, setFilter, clearFilters } = useDataTransferParams();

  const integrationQuery = useGetInitiativeAppIntegrationQuery({ initiativeId, code }, { skip: !code });
  const providerName = integrationQuery.data?.provider?.name;

  const subConnectionsQuery = useListSubConnectionsQuery(
    { initiativeId, code },
    { skip: !code || !isUserStaff },
  );

  const canQuery = Boolean(isUserStaff && childInitiativeId && startDate && endDate);

  const comparisonQuery = useGetDataTransferComparisonQuery(
    { initiativeId, code, childInitiativeId, startDate, endDate },
    { skip: !canQuery },
  );

  const [triggerExport, exportQuery] = useLazyGetDataTransferExportQuery();

  const [exportError, setExportError] = useState<string>();

  const storageKey = `data-transfer-excluded:${code}:${childInitiativeId}`;

  const readExcludedRows = (key: string): Set<string> => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed: unknown = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set<string>(parsed);
        }
      }
    } catch {
      // Corrupt localStorage data; fall back to empty set
    }
    return new Set<string>();
  };

  const [excludedRows, setExcludedRows] = useState<Set<string>>(() => readExcludedRows(storageKey));

  useEffect(() => {
    setExcludedRows(readExcludedRows(storageKey));
  }, [storageKey]);

  // Browser-tab title — makes it possible to tell multiple open comparison
  // tabs apart (per-subsidiary reviews over different date ranges look
  // identical otherwise). Falls back gracefully until subsidiary data
  // arrives. App.tsx resets document.title on every pathname change, so
  // no cleanup is needed when the user navigates away.
  const selectedSubConnection = subConnectionsQuery.data?.find((sc) => sc.initiativeId === childInitiativeId);
  const subsidiaryLabel = selectedSubConnection?.data?.facilityName ?? selectedSubConnection?.externalId ?? selectedSubConnection?.name;
  const subsidiaryExternalId = selectedSubConnection?.externalId;
  const formatShortDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  useEffect(() => {
    if (!subsidiaryLabel || !startDate || !endDate) {
      return;
    }
    document.title = `${subsidiaryLabel} · ${formatShortDate(startDate)}–${formatShortDate(endDate)} · Data Transfer`;
  }, [subsidiaryLabel, startDate, endDate]);

  // File-system-safe slug — keeps letters, digits, dashes, underscores,
  // collapses everything else to a single hyphen. Used for subsidiary and
  // external id segments in the download filename.
  const toFilenameSegment = (value: string) => value.replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '');
  const downloadBaseName = (() => {
    const parts: string[] = ['data-transfer', code];
    if (subsidiaryLabel) {
      parts.push(toFilenameSegment(subsidiaryLabel));
    }
    if (subsidiaryExternalId) {
      parts.push(toFilenameSegment(subsidiaryExternalId));
    }
    if (startDate && endDate) {
      parts.push(`${startDate.slice(0, 10)}_${endDate.slice(0, 10)}`);
    }
    return parts.filter(Boolean).join('_');
  })();

  const toggleExclude = useCallback((key: string) => {
    setExcludedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {
        // Storage unavailable (quota exceeded, private browsing, etc.)
      }
      return next;
    });
  }, [storageKey]);

  const clearExcluded = useCallback(() => {
    setExcludedRows(new Set());
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Storage unavailable (quota exceeded, private browsing, etc.)
    }
  }, [storageKey]);

  const handleExport = useCallback(async () => {
    if (!canQuery) {
      return;
    }
    setExportError(undefined);
    const result = await triggerExport({ initiativeId, code, childInitiativeId, startDate, endDate });
    if (result.data) {
      downloadBlob(result.data, `${downloadBaseName}.xlsx`);
    }
    if (result.error) {
      setExportError('Failed to download export. Please try again.');
    }
  }, [triggerExport, initiativeId, code, childInitiativeId, startDate, endDate, canQuery]);

  if (!isUserStaff) {
    return null;
  }

  return (
    <Dashboard className='w-100'>
      <div className='pl-2 pb-4'>
        <CTAdminBreadcrumbs
          initiativeId={initiativeId}
          breadcrumbs={[
            { label: 'Connections', url: generateUrl(ROUTES.APP_INTEGRATIONS, { initiativeId }) },
            { label: providerName ?? code, url: generateUrl(ROUTES.APP_INTEGRATIONS_VIEW, { initiativeId, code }) },
            { label: 'Data Transfer' },
          ]}
        />
      </div>
      <DashboardSectionTitle title={<>{providerName ?? 'Data Transfer'} — Data Transfer Review <FeatureStability stability='internal' /></>} />
      <DashboardSection>
        <div className='row mb-3'>
          <div className='col-md-6'>
            <label htmlFor='subsidiary-selector' className='form-label fw-bold'>Select Subsidiary</label>
            <QueryWrapper query={subConnectionsQuery} onSuccess={(subConnections) => (
              <select
                id='subsidiary-selector'
                className='form-select'
                value={childInitiativeId}
                onChange={(e) => setChildInitiativeId(e.target.value)}
              >
                <option value=''>Select a subsidiary...</option>
                {subConnections.map((sc) => (
                  <option key={sc._id} value={sc.initiativeId}>
                    {sc.data?.facilityName ?? sc.externalId ?? sc.name}
                  </option>
                ))}
              </select>
            )} />
          </div>
          <div className='col-md-6'>
            <label className='form-label fw-bold'>Date Range</label>
            <TimeRangeSelector
              timeRange={timeRange}
              dateRange={dateRange}
              onChangeDateRange={setDateRange}
            />
          </div>
        </div>

        {canQuery && (
          <QueryWrapper query={comparisonQuery} onSuccess={(data) => {
            const totalRows = data.surveys.reduce((acc, s) => acc + s.rows.length, 0);
            const filteredSurveys = data.surveys.map((s) => ({
              ...s,
              rows: applyFilters(s.rows, filters, search, excludedRows),
            }));
            const filteredRowCount = filteredSurveys.reduce((acc, s) => acc + s.rows.length, 0);

            return (
              <>
                {renderNoticeAlerts(data.notices)}
                {(data.connectionExternalId || data.childExternalId) && (
                  <div className='d-flex gap-3 mb-3'>
                    {data.connectionExternalId && (
                      <small className='text-ThemeTextLight'>
                        Root External ID: <code>{data.connectionExternalId}</code>
                      </small>
                    )}
                    {data.childExternalId && (
                      <small className='text-ThemeTextLight'>
                        Child External ID: <code>{data.childExternalId}</code>
                      </small>
                    )}
                  </div>
                )}
                <DataTransferSummaryCards surveys={data.surveys} />
                <div className='d-flex align-items-center gap-3 mb-3'>
                  <div className='btn-group btn-group-sm'>
                    <button
                      className={`btn ${groupMode === 'survey' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setGroupMode('survey')}
                    >
                      <i className='fa fa-list-alt me-1' />Group by Survey
                    </button>
                    <button
                      className={`btn ${groupMode === 'question' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setGroupMode('question')}
                    >
                      <i className='fa fa-columns me-1' />Group by Question
                    </button>
                  </div>
                </div>
                <DataTransferFilterBar
                  search={search}
                  onSearch={setSearch}
                  filters={filters}
                  onSelect={setFilter}
                  onClear={clearFilters}
                  onClearExcluded={clearExcluded}
                  totalRows={totalRows}
                  filteredRows={filteredRowCount}
                  excludedCount={excludedRows.size}
                />
                <div className='d-flex justify-content-end gap-2 mb-3'>
                  <button
                    className='btn btn-outline-secondary btn-sm'
                    onClick={() => comparisonQuery.refetch()}
                    disabled={comparisonQuery.isFetching}
                  >
                    <i className={`fa fa-refresh me-1 ${comparisonQuery.isFetching ? 'fa-spin' : ''}`} />
                    Refresh
                  </button>
                  <button
                    className='btn btn-outline-primary btn-sm'
                    onClick={handleExport}
                    disabled={exportQuery.isFetching}
                  >
                    <i className={`fa fa-download me-1 ${exportQuery.isFetching ? 'fa-spin' : ''}`} />
                    Download XLSX
                  </button>
                  <DataTransferCsvExport surveys={filteredSurveys} filename={`${downloadBaseName}${filters.validation ? `_${filters.validation}` : ''}.csv`} subsidiaryName={subsidiaryLabel} />
                  {exportError && (
                    <small className='text-ThemeDangerMedium'>
                      <i className='fa fa-exclamation-circle me-1' />{exportError}
                    </small>
                  )}
                </div>
                {groupMode === 'survey' ? (
                  filteredSurveys.map((survey) => {
                    const discrepancies = survey.rows.filter((r) => r.hasDiscrepancy).length;
                    const matching = survey.rows.filter((r) => !r.hasDiscrepancy && r.survey.value !== undefined && r.integration.value !== undefined).length;
                    return (
                      <div key={survey.surveyId || 'no-survey'} className='mb-4'>
                        <h6 className='fw-bold'>
                          {survey.label}
                          {survey.effectiveDate && (
                            <small className='text-ThemeTextLight ms-2'>
                              ({new Date(survey.effectiveDate).toLocaleDateString()})
                            </small>
                          )}
                          <span className='badge background-ThemeIconSecondary text-ThemeTextWhite ms-2'>
                            {survey.rows.length} rows
                          </span>
                          {matching > 0 && (
                            <span className='small text-ThemeSuccessMedium ms-2'>
                              <i className='fa fa-check me-1' />{matching} matching
                            </span>
                          )}
                          {discrepancies > 0 && (
                            <span className='small text-ThemeDangerMedium ms-2'>
                              <i className='fa fa-exclamation-circle me-1' />{discrepancies} differing
                            </span>
                          )}
                        </h6>
                        <DataTransferTable rows={survey.rows} childInitiativeId={childInitiativeId} excludedRows={excludedRows} onToggleExclude={toggleExclude} />
                      </div>
                    );
                  })
                ) : (
                  <DataTransferQuestionView surveys={data.surveys} filteredSurveys={filteredSurveys} childInitiativeId={childInitiativeId} />
                )}
              </>
            );
          }} />
        )}
      </DashboardSection>
    </Dashboard>
  );
};
